// src/components/payment/PaymentModal.tsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Lock,
  Sparkles,
  Heart,
  Gift,
  Star,
  AlertCircle,
  ChevronDown,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { paystackService } from "../../services/paystack.service";
import { paymentCalculator, PaymentBreakdown } from "../../services/payment-calculator.service";
import { paymentStateMachine, PaymentState } from "../../services/payment-state.service";
import { PAYMENT_CONFIG, PAYMENT_MESSAGES } from "../../config/payment.config";
import { PaymentError, PaymentErrorHandler } from "../../utils/payment-errors";
import { ExtraContribution } from "./ExtraContribution";
import { whitelistService } from "../../services/whitelist.service";
import { cardService } from "../../services/card.service";
import toast from "react-hot-toast";
import { cn } from "../../utils/helpers";
import { tiktokPixelService } from "../../services/tiktok-pixel.service";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  cardId: string;
  email: string;
  onSuccess: () => void;
  recipientName?: string;
  bouquetAmount?: number;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  cardId,
  email,
  onSuccess,
  recipientName,
  bouquetAmount = 0,
}) => {
  const { user } = useAuth();
  const [paymentState, setPaymentState] = useState<PaymentState>("idle");
  const [error, setError] = useState<PaymentError | null>(null);
  const [extraContribution, setExtraContribution] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isFree, setIsFree] = useState(false);
  const [breakdown, setBreakdown] = useState<PaymentBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load breakdown with discount
  useEffect(() => {
    const loadBreakdown = async () => {
      setIsLoading(true);
      try {
        const calculated = await paymentCalculator.calculatePaymentBreakdownWithDiscount(
          bouquetAmount,
          extraContribution,
          email
        );
        setBreakdown(calculated);
        setIsFree(calculated.total === 0);
      } catch (error) {
        console.error("Error calculating breakdown:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBreakdown();
  }, [bouquetAmount, extraContribution, email]);

  useEffect(() => {
    const unsubscribe = paymentStateMachine.subscribe(setPaymentState);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (paymentState === "success") {
      onSuccess();
    }
  }, [paymentState, onSuccess]);

  const handleFreeAccess = async () => {
    if (!user?.id) {
      setError(PaymentErrorHandler.handle({ code: "USER_NOT_AUTHENTICATED" }));
      return;
    }

    if (!breakdown) {
      setError(PaymentErrorHandler.handle({ code: "PAYMENT_INITIALIZATION_FAILED" }));
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await paymentStateMachine.transition("processing");

      const success = await cardService.markCardAsPaid(cardId, "free_access");

      if (success) {
        await whitelistService.incrementUsage(email);

        tiktokPixelService.track(
          "UnlockFreeAccess",
          {
            contents: [
              {
                content_id: cardId,
                content_type: "product",
                content_name: "Free Access Experience",
              },
            ],
          },
          `free_access:${cardId}`
        );

        toast.success("Your experience is ready to share.");
        onClose();
        onSuccess();
      } else {
        throw new Error("Failed to activate card");
      }
    } catch (err) {
      await paymentStateMachine.transition("failed");
      const handledError = PaymentErrorHandler.handle(err);
      setError(handledError);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!user?.id) {
      setError(PaymentErrorHandler.handle({ code: "USER_NOT_AUTHENTICATED" }));
      return;
    }

    if (!breakdown) {
      setError(PaymentErrorHandler.handle({ code: "PAYMENT_INITIALIZATION_FAILED" }));
      return;
    }

    setError(null);
    try {
      tiktokPixelService.trackAddPaymentInfo(
        {
          content_id: cardId,
          content_type: "product",
          content_name: recipientName ? `Experience for ${recipientName}` : "LRNSY Experience",
        },
        breakdown.total,
        "KES",
        `addpaymentinfo:${cardId}`
      );

      await paystackService.initializePayment({
        cardId,
        email,
        userId: user.id,
        bouquetAmount: bouquetAmount || 0,
        extraContribution,
        metadata: {
          recipientName,
          extraContribution,
          includeBouquet: bouquetAmount > 0,
        },
      });
    } catch (err) {
      const handledError = PaymentErrorHandler.handle(err);
      setError(handledError);
    }
  };

  if (!breakdown) {
    return null;
  }

  const features = [
    {
      icon: Gift,
      label: "Bouquet",
      value: bouquetAmount > 0 ? paymentCalculator.formatPrice(bouquetAmount) : "Not included",
    },
    {
      icon: Sparkles,
      label: "Petals",
      value: breakdown.petals > 0 ? `${breakdown.petals} petals` : "None",
    },
    {
      icon: Star,
      label: "Experience Access",
      value: paymentCalculator.formatPrice(breakdown.totalPlatformFee),
    },
    {
      icon: Lock,
      label: "Private Link",
      value: "Included",
    },
  ];

  const isProcessing =
    paymentState === "initializing" || paymentState === "processing" || isLoading;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative w-full max-w-md max-h-[90vh] flex flex-col bg-gradient-to-br from-surface/95 to-surface/90 backdrop-blur-2xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 -right-12 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-12 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />
            </div>

            {/* Header */}
            <div className="relative flex-shrink-0 p-4 sm:p-5 border-b border-white/10">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed z-10"
              >
                <X size={18} className="text-white" />
              </button>

              <div className="flex items-center gap-2 sm:gap-3 pr-8">
                <div
                  className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center border border-white/20 flex-shrink-0",
                    isFree
                      ? "bg-gradient-to-br from-green-500/20 to-emerald-500/20"
                      : "bg-gradient-to-br from-primary/20 to-secondary/20"
                  )}
                >
                  {isFree ? (
                    <CheckCircle size={20} className="text-green-400" />
                  ) : (
                    <Lock size={20} className="text-primary" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-black text-base sm:text-xl text-white truncate">
                    {isFree
                      ? "Complimentary Access 🎉"
                      : paymentState === "success"
                        ? "Payment Complete!"
                        : "Complete Your Experience"}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-text-tertiary truncate">
                    {isFree
                      ? "This experience is available to you at no cost"
                      : paymentState === "success"
                        ? "Redirecting you..."
                        : "One-time payment • Secure access"}
                  </p>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 sm:space-y-5">
              {/* Discount Banner */}
              {breakdown.discountPercentage > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-3"
                >
                  <div className="flex items-center gap-2">
                    <Star size={16} className="text-purple-400" />
                    <p className="text-xs text-purple-300">
                      {breakdown.discountPercentage === 100
                        ? "You’ve received complimentary access to this experience."
                        : `${breakdown.discountPercentage}% discount applied`}
                    </p>
                  </div>
                  {breakdown.originalTotal > 0 && breakdown.discountPercentage === 100 && (
                    <p className="text-[10px] text-purple-400/60 mt-1">
                      Original price: {paymentCalculator.formatPrice(breakdown.originalTotal)}
                    </p>
                  )}
                </motion.div>
              )}

              {/* Error message */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2"
                  >
                    <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                    <p className="text-[11px] sm:text-xs text-red-500">{error.message}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Recipient preview */}
              {recipientName && (
                <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-white/10">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart size={14} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[8px] sm:text-[9px] font-mono text-text-tertiary">FOR</p>
                      <p className="font-black text-sm sm:text-base text-white truncate">
                        {recipientName}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment features */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {features.map((feature, i) => (
                  <div
                    key={i}
                    className="bg-white/5 backdrop-blur-sm rounded-xl p-2 sm:p-3 border border-white/10"
                  >
                    <feature.icon size={12} className="text-primary mb-1" />
                    <span className="text-[7px] sm:text-[8px] font-bold uppercase tracking-wider text-white/60 block">
                      {feature.label}
                    </span>
                    <span className="text-[11px] sm:text-xs font-bold text-white break-words">
                      {feature.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Extra Contribution - Only show if not free */}
              {PAYMENT_CONFIG.allowExtraContribution && paymentState !== "success" && !isFree && (
                <ExtraContribution value={extraContribution} onChange={setExtraContribution} />
              )}

              {/* Price breakdown */}
              <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/10 overflow-hidden">
                <button
                  onClick={() => setShowBreakdown(!showBreakdown)}
                  className="w-full p-3 sm:p-4 flex items-center justify-between"
                >
                  <span className="text-[9px] sm:text-[10px] font-mono text-text-tertiary">
                    {isFree ? "COMPLIMENTARY ACCESS" : "EXPERIENCE SUMMARY"}
                  </span>
                  {!isFree && (
                    <ChevronDown
                      size={14}
                      className={`text-white/60 transition-transform duration-300 ${
                        showBreakdown ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>

                <AnimatePresence>
                  {(showBreakdown || isFree) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-3 sm:px-4 pb-3 sm:pb-4"
                    >
                      <div className="space-y-2 pt-2 border-t border-white/10">
                        {isFree ? (
                          <div className="py-2 text-center">
                            <p className="text-green-400 text-sm">
                              This experience is fully complimentary
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between text-[11px] sm:text-xs">
                              <span className="text-text-tertiary">
                                Experience Access (min. {PAYMENT_CONFIG.basePlatformFee} KES)
                              </span>
                              <span className="font-bold text-white">
                                {paymentCalculator.formatPrice(breakdown.totalPlatformFee)}
                              </span>
                            </div>

                            {extraContribution > 0 && (
                              <div className="flex justify-between text-[11px] sm:text-xs text-primary/80">
                                <span className="text-text-tertiary">Your extra contribution</span>
                                <span className="font-bold">
                                  +{paymentCalculator.formatPrice(extraContribution)}
                                </span>
                              </div>
                            )}

                            {bouquetAmount > 0 && (
                              <div className="flex justify-between text-[11px] sm:text-xs">
                                <span className="text-text-tertiary">Bouquet</span>
                                <span className="font-bold text-white">
                                  {paymentCalculator.formatPrice(breakdown.bouquetAmount)}
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between text-[11px] sm:text-xs">
                              <span className="text-text-tertiary">
                                VAT ({PAYMENT_CONFIG.vatRate * 100}%)
                              </span>
                              <span className="font-bold text-white">
                                {paymentCalculator.formatPrice(breakdown.vat)}
                              </span>
                            </div>

                            <div className="border-t border-white/10 pt-2 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-white text-xs sm:text-sm">
                                  Total
                                </span>
                                <span className="font-black text-lg sm:text-xl text-primary">
                                  {paymentCalculator.formatPrice(breakdown.total)}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Edit notice */}
              <p className="text-center text-[8px] sm:text-[9px] text-yellow-400/60 bg-yellow-400/5 p-2 rounded-lg">
                ⚡ You can still edit this experience until the link is opened
              </p>
            </div>

            {/* Footer */}
            <div className="relative flex-shrink-0 p-4 sm:p-5 border-t border-white/10 bg-surface/95">
              <button
                onClick={isFree ? handleFreeAccess : handlePayment}
                disabled={isProcessing || paymentState === "success"}
                className={cn(
                  "w-full group relative overflow-hidden px-4 py-3 sm:py-4 rounded-xl font-black text-xs sm:text-sm uppercase tracking-wider shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                  isFree
                    ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-green-500/30 hover:shadow-green-500/40"
                    : "bg-gradient-to-r from-primary to-secondary text-white shadow-primary/30 hover:shadow-primary/40"
                )}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                    />
                    <span>{PAYMENT_MESSAGES.PROCESSING}</span>
                  </div>
                ) : isFree ? (
                  <>
                    <CheckCircle size={14} className="inline mr-1.5" />
                    Unlock Complimentary Access
                  </>
                ) : (
                  <>
                    <Lock size={14} className="inline mr-1.5" />
                    Unlock for {paymentCalculator.formatPrice(breakdown.total)}
                  </>
                )}
              </button>

              <p className="text-[7px] sm:text-[8px] text-center text-text-tertiary mt-2">
                By continuing, you agree to our Terms of Service
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
